// MobileSignIn.js
// Phone sign-in (docs/mobile-view/mockups 1a and 1c). Field staff use email or
// phone number alone; office accounts use email and continue to
// a password step. LoginPage shows this only on small screens; the desktop
// login is unchanged.
import React, { useState, useContext } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import api from '../../../authentication/axiosInstance';
import { FIELD_STAFF_HOME } from '../../../authentication/roles';
import companyLogo from '../../../assets/company-logo.png';
import heroImage from '../../../assets/login-page.jpg';
import styles from './MobileSignIn.module.css';

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const validPhone = (value) => {
  if (!/^\+?[0-9() .-]+$/.test(value)) return false;
  const digits = value.replace(/[^0-9]/g, '');
  return (digits.length === 11 && digits.startsWith('1')) || (digits.length === 10 && !value.startsWith('+'));
};
const NOT_FOUND_MESSAGE = "We couldn't find that email or phone number. Check it or call dispatch.";
const CONNECTION_MESSAGE = 'Something went wrong. Check your connection and try again.';

const BrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" stroke="#B8964E" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 27V5h24v22" />
    <path d="M8 27V10h14v17" />
    <path d="M3 27l12-9 12 9" />
  </svg>
);

const MobileSignIn = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const navigate = useNavigate();
  const { setIsAuthenticated, setUserRole, checkAuth } = useContext(AuthContext);

  const goTo = (nextStep) => {
    setError('');
    setPassword('');
    setShowPassword(false);
    setStep(nextStep);
  };

  const validEmail = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email.');
      return null;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError("That doesn't look like an email address.");
      return null;
    }
    return trimmed;
  };

  const finishSignIn = async (data, destination) => {
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setIsAuthenticated(true);
    setUserRole(data.role);
    await checkAuth();
    navigate(destination);
  };

  const handleContinue = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || (!EMAIL_PATTERN.test(trimmed) && !validPhone(trimmed))) {
      setError(trimmed ? 'Enter a valid email or a 10-digit phone number (optional +1).' : 'Enter your email or phone number.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/users/email-sign-in/', { identifier: trimmed });
      if (data.next === 'signed_in') {
        await finishSignIn(data, FIELD_STAFF_HOME);
        return;
      }
      setEmail(data.email || trimmed);
      goTo('password');
    } catch (requestError) {
      const status = requestError.response?.status;
      if (status === 404) {
        setError(requestError.response.data?.error || NOT_FOUND_MESSAGE);
      } else if (status === 429) {
        setError('Too many tries. Wait a minute and try again.');
      } else {
        setError(CONNECTION_MESSAGE);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    if (!password) {
      setError('Enter your password.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const { data } = await api.post('/users/login/', { email, password });
      await finishSignIn(data, '/');
    } catch (requestError) {
      setError(requestError.response?.status === 400 ? 'Incorrect password. Try again.' : CONNECTION_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestReset = async (event) => {
    event.preventDefault();
    const trimmed = validEmail();
    if (!trimmed) return;

    setSubmitting(true);
    try {
      await api.post('/users/request-reset-password/', { email: trimmed });
      setNotice('Reset link sent! Please check your email.');
      goTo('email');
    } catch (requestError) {
      setError('Failed to send reset link.');
      console.error('Reset link request failed:', requestError);
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage = error && (
    <p id="sign-in-error" role="alert" className={styles.fieldError}>{error}</p>
  );

  const emailField = (autoFocus) => (
    <div className={styles.fieldGroup}>
      <label htmlFor="email" className={styles.fieldLabel}>{step === 'email' ? 'Email or phone number' : 'Email'}</label>
      <input
        id="email"
        type={step === 'email' ? 'text' : 'email'}
        inputMode={step === 'email' ? 'text' : 'email'}
        autoComplete="username"
        autoFocus={autoFocus}
        placeholder={step === 'email' ? 'Email or phone number' : 'you@facilitycare.ca'}
        autoCapitalize="none"
        spellCheck={false}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? 'sign-in-error' : undefined}
        className={styles.input}
      />
    </div>
  );

  const stepHeader = (backLabel) => (
    <div className={styles.stepHeader}>
      <button type="button" className={styles.backButton} aria-label={backLabel} onClick={() => goTo('email')}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 6l-6 6 6 6" />
        </svg>
      </button>
      <img src={companyLogo} alt="" className={styles.stepLogo} />
    </div>
  );

  return (
    <main className={styles.page}>
      {step === 'email' && (
        <div className={styles.hero} aria-hidden="true">
          <img src={heroImage} alt="" className={styles.heroImage} />
          <div className={styles.heroOverlay} />
          <div className={styles.heroBrand}>
            <span className={styles.heroMark}><BrandMark /></span>
            <span className={styles.heroTitle}>Facilitate</span>
            <span className={styles.heroTagline}>FACILITY CARE</span>
          </div>
        </div>
      )}

      <div className={step === 'email' ? `${styles.sheet} ${styles.sheetOverHero}` : styles.sheet}>
        {step === 'email' && (
          <form className={styles.form} onSubmit={handleContinue} noValidate>
            <h1 className={styles.heading}>Welcome back</h1>
            {emailField(true)}
            {errorMessage}
            <button type="submit" className={styles.primaryButton} disabled={submitting}>
              {submitting ? 'Checking…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'password' && (
          <form className={styles.form} onSubmit={handleSignIn} noValidate>
            {stepHeader('Back')}
            <div>
              <h1 className={styles.heading}>Enter your password</h1>
              <p className={styles.lead}>Office and admin accounts use a password.</p>
            </div>
            <div className={styles.emailChip}>
              <span className={styles.emailChipInitial} aria-hidden="true">{email.charAt(0).toUpperCase()}</span>
              <span className={styles.emailChipText}>{email}</span>
              <button type="button" className={styles.chipButton} onClick={() => goTo('email')}>Change</button>
            </div>
            {/* Lets password managers pair the password with this email. */}
            <input type="email" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" className={styles.visuallyHidden} />
            <div className={styles.fieldGroup}>
              <label htmlFor="password" className={styles.fieldLabel}>Password</label>
              <div className={styles.passwordField}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'sign-in-error' : undefined}
                  className={styles.input}
                />
                <button
                  type="button"
                  className={styles.revealButton}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((shown) => !shown)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              <button type="button" className={styles.linkButton} onClick={() => goTo('forgot')}>Forgot password?</button>
            </div>
            {errorMessage}
            <button type="submit" className={styles.primaryButton} disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {step === 'forgot' && (
          <form className={styles.form} onSubmit={handleRequestReset} noValidate>
            {stepHeader('Back to sign in')}
            <div>
              <h1 className={styles.heading}>Forgot password</h1>
              <p className={styles.lead}>Enter your email to receive a reset link.</p>
            </div>
            {emailField(false)}
            {errorMessage}
            <button type="submit" className={styles.primaryButton} disabled={submitting}>Send reset link</button>
          </form>
        )}

        <p className={styles.footer}>&copy; {new Date().getFullYear()} Facility Care</p>
      </div>

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice('')}>
        <Alert onClose={() => setNotice('')} severity="success">{notice}</Alert>
      </Snackbar>
    </main>
  );
};

export default MobileSignIn;
